import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { useAuthRequest } from 'Src/feature/auth-request/useAuthRequest';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { AuthorizationRequestAnswered, AuthorizationRequestCreated } from 'Src/feature/notification/types';
import { useToast } from 'Src/feature/toasts/useToast';
import { ApprovableEntitlementId, AuthorizationRequestFor, AuthRequestCheckDiscountTarget, AuthRequestCheckSurchargeTarget, AuthRequestNotificationLabelKey, AuthRequestTimeClockOverride } from 'Src/models/AuthorizationRequest';
import useRoute from 'Src/routes/useRoute';

import { NotificationUI } from './useNotification';

import styles from './AuthRequestToastNotification.module.scss';

const sectionId = 'AuthRequestToastNotification';

interface AuthRequestToastNotificationProps {
  notification: NotificationUI<AuthorizationRequestCreated | AuthorizationRequestAnswered>;
  onAction?: () => void;
}

const AuthRequestToastNotification: FC<AuthRequestToastNotificationProps> = ({
  notification,
  onAction,
}) => {
  const { t } = useTranslation();
  const { navigate, crumbs, route } = useRoute();
  const toast = useToast();

  const { data } = notification;

  const answerAuth = useAuthRequest.mutate.answer();
  const employeeGetAllMap = useEmployee.query.map();

  let labelKey = `approvalRequest.notificationHeaders.${data.entitlementId as ApprovableEntitlementId}`;
  let expectedStartTime = undefined as string | undefined;
  let currentTime = undefined as string | undefined;
  let targetCheckId = undefined as number | undefined;
  let statusReason = undefined as string | undefined;
  const targetCheckLabel = undefined as string | undefined;

  let navigationTarget: string | undefined;
  switch (data.entitlementId) {
    case 'pos.checks.payment.accept.execute':
    case 'pos.checks.payments.refund.execute':
    case 'pos.checks.reopen.execute':
    case 'pos.checks.void':
    case 'pos.checks.discounts.create':
    case 'pos.checks.discounts.delete':
    case 'pos.checks.86Items.create':
    case 'pos.checks.items.void': {
      const voidTarget = data as AuthorizationRequestFor<'pos.checks.items.void'>;
      const items = voidTarget.target?.items;
      currentTime = data.createdAt ? DateTime.fromMillis(data.createdAt).toFormat('yyyy-MM-dd hh:mm') : undefined;
      targetCheckId = voidTarget.target.id;
      statusReason = items?.length ? items[0].statusReason : undefined;
      navigationTarget = crumbs.pos.check.main.interpolate({
        checkId: targetCheckId,
        // start w/ single select mode for check item
        itemSelect: route.params.value.one,
      });
      break;
    }
    case 'pos.checks.discounts.manage': {
      currentTime = data.createdAt ? DateTime.fromMillis(data.createdAt).toFormat('yyyy-MM-dd hh:mm') : undefined;
      const discountTarget = data.target as AuthRequestCheckDiscountTarget;
      const isAdd = discountTarget.isAdd;
      const discountType = discountTarget.discount?.type;
      const isPercentage = discountType !== 'Amount';
      if (isAdd) {
        labelKey = isPercentage ? `${labelKey}.addPercentage` : `${labelKey}.add`;
      } else {
        labelKey = isPercentage ? `${labelKey}.removePercentage` : `${labelKey}.remove`;
      }
      break;
    }
    case 'pos.timeClockOverride.execute':
      if ((data.target as AuthRequestTimeClockOverride).isEarly && (data.target as AuthRequestTimeClockOverride).isScheduled) {
        labelKey = `${labelKey}.early`;
        expectedStartTime = DateTime.fromISO((data.target as AuthRequestTimeClockOverride).expectedStartTime ?? '').toFormat('hh:mm a');
        currentTime = DateTime.fromISO((data.target as AuthRequestTimeClockOverride).currentTime).toFormat('hh:mm a');
      } else if ((data.target as AuthRequestTimeClockOverride).isLate && (data.target as AuthRequestTimeClockOverride).isScheduled) {
        labelKey = `${labelKey}.late`;
        expectedStartTime = DateTime.fromISO((data.target as AuthRequestTimeClockOverride).expectedStartTime ?? '').toFormat('hh:mm a');
        currentTime = DateTime.fromISO((data.target as AuthRequestTimeClockOverride).currentTime).toFormat('hh:mm a');
      } else {
        labelKey = `${labelKey}.unscheduled`;
      }
      break;
    case 'pos.checks.fees.manage':
      navigationTarget = crumbs.pos.check.main.interpolate({
        checkId: (data as AuthorizationRequestFor<typeof data.entitlementId>).target.id,
        // start w/ single select mode for check item
        itemSelect: route.params.value.one,
      });
      labelKey = `${labelKey}.${(data.target as AuthRequestCheckSurchargeTarget).action}`;
      break;
    default:
      navigationTarget = undefined;
  }

  let itemNames = undefined as undefined | string;
  if (data.entitlementId === 'pos.checks.items.void' || data.entitlementId === 'pos.checks.86Items.create') {
    itemNames = (data as AuthorizationRequestFor<typeof data.entitlementId>).target.items.map((i) => i.menuItem.combinedName).join(', ');
  }

  const employee = employeeGetAllMap.data?.get(data.requestingEmployeeId);

  return (
    <Flex
      horizontal
      alignItems="center"
      justifyContent="space-between"
      className={styles.root}
    >
      <Flex gap="sm">
        <Copy variant="body" weight="md" value={targetCheckLabel ? t('approvalRequest.labels.approvalRequestedCheck', { checkLabel: targetCheckLabel }) : t('approvalRequest.labels.approvalRequested')} />
        <Copy variant="body" size="sm" weight="regular">
          <Trans
            data-testid={sectionId}
            i18nKey={labelKey as AuthRequestNotificationLabelKey}
            values={{
              employeeName: employee
                ? t('labels.util.employeeFullName', employee)
                : t('notification.authRequest.unknownEmployee'),
              target: data.target,
              itemNames,
              expectedStartTime,
              currentTime,
              statusReason,
            }}
            components={{
              primary: <Copy variant="body" size="sm" weight="regular" />,
              navigate: (
                <Copy
                  variant="body"
                  size="sm"
                  weight="semi-bold"
                  hidden={!navigationTarget}
                  onClick={() => {
                    if (navigationTarget) navigate(navigationTarget);
                    if (onAction) onAction();
                  }}
                />
              ),
            }}
          />
        </Copy>
      </Flex>
      <Flex horizontal gap="sm">
        <CTA
          variant="outlined"
          onClick={() => {
            answerAuth.mutate({ authRequestId: data.id as number, outcome: 'Denied' }, {
              onSuccess: () => {
                if (onAction) onAction();
              },
              onError: (e) => {
                toast.show.serviceError(e, { id: `${sectionId}-answerAuth-Denied` });
                if (onAction) onAction();
              },
            });
          }}
          label={t('labels.deny')}
        />
        <CTA
          color="textPrimary"
          onClick={() => {
            answerAuth.mutate({ authRequestId: data.id as number, outcome: 'Approved' }, {
              onSuccess: () => {
                if (onAction) onAction();
              },
              onError: (e) => {
                toast.show.serviceError(e, { id: `${sectionId}-answerAuth-Approved` });
                if (onAction) onAction();
              },
            });
          }}
          label={t('labels.approve')}
        />
      </Flex>
    </Flex>
  );
};

export type { AuthRequestToastNotificationProps };
export { AuthRequestToastNotification, sectionId };
